import { useState } from "react";
import { ArrowDown, ArrowRight, RotateCcw } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const EXAMPLES = [
  "A failed transaction may actually be a stale-data problem.",
  "A software error may originate in an upstream integration.",
  "An incorrect decision may result from accurate information being interpreted without the right context.",
  "A recurring operational issue may be caused by workflow, training, policy interpretation, data quality, system behavior — or several of these working together.",
];

const CHAIN = [
  { name: "People", desc: "Roles, incentives, workload, training — the human conditions work happens under." },
  { name: "Process", desc: "Handoffs, exception paths, and policy interpretation in practice." },
  { name: "Data", desc: "Freshness, completeness, lineage — what the system believes to be true." },
  { name: "Integration", desc: "The interfaces where meaning is translated — or lost — between systems." },
  { name: "System", desc: "Application behavior: rules, thresholds, and error states as configured." },
  { name: "Decision", desc: "The judgment — human or automated — the information produces." },
  { name: "Outcome", desc: "What actually happens to a citizen, customer, case, or operation." },
];

export default function ProblemSection() {
  const [active, setActive] = useState(CHAIN.length - 1);

  return (
    <section data-testid="problem-section" className="border-b border-line">
      <div className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="mb-6 flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-slate-500">
                <span className="h-px w-10 bg-slate-300" aria-hidden="true" />
                01 — THE PROBLEM
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
                The symptom is not always the source.
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-8 leading-relaxed text-slate-600">
                Fixing the visible symptom without understanding the underlying system often
                moves the problem instead of solving it.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="border-t border-line">
              {EXAMPLES.map((ex, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <p className="border-b border-line py-5 font-serif text-lg italic leading-relaxed text-slate-700 sm:text-xl">
                    {ex}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <Reveal delay={0.1} className="mt-20">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] tracking-[0.3em] text-slate-400">
              [FIG.02] — THE CAUSAL CHAIN
            </p>
            <p className="hidden font-mono text-[10px] tracking-[0.3em] text-slate-400 sm:block">
              TRACE BACKWARD ←
            </p>
          </div>

          <div className="mt-6 flex flex-col md:flex-row md:items-stretch" role="group" aria-label="System causal chain">
            {CHAIN.map((node, i) => {
              const isActive = i === active;
              const onPath = i < active;
              return (
                <div key={node.name} className="flex flex-col md:flex-1 md:flex-row md:items-stretch">
                  <button
                    type="button"
                    data-testid={`chain-node-${node.name.toLowerCase()}`}
                    onMouseEnter={() => setActive(i)}
                    onFocus={() => setActive(i)}
                    onClick={() => setActive(i)}
                    className={`flex-1 border p-4 text-left transition-colors duration-300 focus:outline-none ${
                      isActive
                        ? "border-navy bg-navy text-white"
                        : onPath
                          ? "border-slate-400 bg-white text-ink"
                          : "border-line bg-white text-slate-400"
                    }`}
                  >
                    <span
                      className={`block font-mono text-[10px] ${
                        isActive ? "text-slate-300" : "text-slate-400"
                      }`}
                    >
                      0{i + 1}
                    </span>
                    <span className="mt-1 block text-sm font-medium tracking-wide">
                      {node.name}
                    </span>
                  </button>
                  {i < CHAIN.length - 1 && (
                    <div className="flex items-center justify-center py-1 md:px-1.5 md:py-0" aria-hidden="true">
                      <ArrowRight
                        className={`hidden h-3.5 w-3.5 md:block ${
                          i < active ? "text-accent" : "text-slate-300"
                        }`}
                      />
                      <ArrowDown
                        className={`h-3.5 w-3.5 md:hidden ${
                          i < active ? "text-accent" : "text-slate-300"
                        }`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex min-h-24 flex-col gap-4 border border-line bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-slate-400" data-testid="chain-trace-position">
                TRACE POSITION — 0{active + 1} / 07
              </p>
              <p className="mt-2 font-serif text-xl text-ink" data-testid="chain-active-name">
                {CHAIN[active].name}
              </p>
              <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-600">
                {CHAIN[active].desc}
              </p>
            </div>
            {active < CHAIN.length - 1 ? (
              <button
                type="button"
                data-testid="chain-reset-button"
                onClick={() => setActive(CHAIN.length - 1)}
                className="inline-flex w-fit items-center gap-2 border border-slate-300 px-4 py-2.5 text-xs tracking-wide text-ink transition-colors hover:border-ink"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Restart at the outcome
              </button>
            ) : (
              <p className="max-w-xs text-xs leading-relaxed text-slate-500">
                An undesirable outcome can be traced backward through the chain. Move along
                the chain to trace toward the cause.
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
